import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterSeason, TyresType } from '@enums/Filter';
import { diskManufacturerSort } from '@app/helpers/diskManufacturerSort';
import { IAkbFilter, IAutoFilter, IDisksFilter, IOilsFilter, ITyresFilter, ITruckTiresParametrs } from '@models/Filter/mainFilte';
import {
  fetchAkbData,
  fetchAutoMark,
  fetchAutoModel,
  fetchAutoModification,
  fetchAutoYears,
  fetchDiscsData,
  fetchTyresData,
} from '@redux/slices/mainFilter/api';

export interface KeyValue {
  key: string;
  value: any;
}

interface IState {
  TruckTiresParametrs: ITruckTiresParametrs;
  AutoFilter: IAutoFilter;
  TyresFilter: ITyresFilter;
  DisksFilter: IDisksFilter;
  AkbFilter: IAkbFilter;
  OilsFilter: IOilsFilter;
  defaultSeason: string;
  isLoadingMark: boolean;
  isLoadingModel: boolean;
  isLoadingYear: boolean;
  isLoadingModification: boolean;
  isLoadingDataDiscs: boolean;
  isLoadingDataTyres: boolean;
  isLoadingDataAkb: boolean;
  openTab?: number;
}

const initialState: IState = {
  TruckTiresParametrs: {
    listWidth: [],
    listHeight: [],
    listDiameter: [],
    listManufacturer: [],
    listType: [],
    listHere: [],
    width: '',
    height: '',
    diameter: '',
    manufacturer: '',
    type: '',
    axle: '',
  },
  AutoFilter: {
    listMark: [],
    listModel: [],
    listYear: [],
    listModify: [],
    Mark: '',
    Model: '',
    Year: '',
    Modify: '',
    seasonFilter: FilterSeason.SUMMER,
    tyresTypeFilter: TyresType.noMatter,
    free: false,
  },
  TyresFilter: {
    listWidth: [],
    listHeight: [],
    listRadius: [],
    listCompany: [],
    Width: 205,
    Height: 55,
    Radius: '16',
    Company: '',
    seasonFilter: FilterSeason.SUMMER,
    tyresTypeFilter: TyresType.noMatter,
    RunFlat: false,
    free: false,
    sale: false,
    commers: false,
  },
  DisksFilter: {
    listRadius: [],
    listPCD: [],
    listType: [],
    listCompany: [],
    Radius: null,
    Type: null,
    PCD: null,
    Company: null,
    sale: false,
    free: false,
  },
  AkbFilter: {
    listSize: [],
    listPolarity: [],
    listPower: [],
    listCompany: [],
    size: '',
    polarity: '',
    power: '',
    company: '',
  },
  OilsFilter: {
    listViscosity: [],
    listApiSpec: [],
    listAcea: [],
    listCompany: [],
    listVolumes: [],
    viscosity: '',
    apiSpec: '',
    acea: '',
    company: '',
    volume: '',
  },
  defaultSeason: FilterSeason.SUMMER,
  isLoadingMark: false,
  isLoadingModel: false,
  isLoadingYear: false,
  isLoadingModification: false,
  isLoadingDataDiscs: false,
  isLoadingDataTyres: false,
  isLoadingDataAkb: false,
  openTab: null,
};

export const filterSlice = createSlice({
  name: 'mainFilter',
  initialState,
  reducers: {
    setOpenTab: (state, action: PayloadAction<number>) => {
      state.openTab = state.openTab === action.payload ? null : action.payload;
    },
    setAutoFilter: (state, action: PayloadAction<KeyValue>) => {
      const { key, value } = action.payload;
      state.AutoFilter[key] = value;
    },
    setTyresFilter: (state, action: PayloadAction<KeyValue>) => {
      const { key, value } = action.payload;
      state.TyresFilter[key] = value;
    },
    setDisksFilter: (state, action: PayloadAction<KeyValue>) => {
      const { key, value } = action.payload;
      state.DisksFilter[key] = value;
    },
    setAkbFilter: (state, action: PayloadAction<KeyValue>) => {
      const { key, value } = action.payload;
      state.AkbFilter[key] = value;
    },
    setOilsFilter: (state, action: PayloadAction<KeyValue>) => {
      const { key, value } = action.payload;
      state.OilsFilter[key] = value;
    },
    setTiresParametrsFilter: (state, action: PayloadAction<KeyValue>) => {
      const { key, value } = action.payload;
      state.TruckTiresParametrs[key] = value;
    },
    setAutoMark: (state, action: PayloadAction<string>) => {
      state.AutoFilter.Mark = action.payload;
      state.AutoFilter.Model = '';
      state.AutoFilter.listModel = [];
      state.AutoFilter.Year = '';
      state.AutoFilter.listYear = [];
      state.AutoFilter.Modify = '';
      state.AutoFilter.listModify = [];
    },
    setAutoModel: (state, action: PayloadAction<string>) => {
      state.AutoFilter.Model = action.payload;
      state.AutoFilter.Year = '';
      state.AutoFilter.listYear = [];
      state.AutoFilter.Modify = '';
      state.AutoFilter.listModify = [];
    },
    setAutoYear: (state, action: PayloadAction<string>) => {
      state.AutoFilter.Year = action.payload;
      state.AutoFilter.Modify = '';
      state.AutoFilter.listModify = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAutoMark.pending, state => {
        state.isLoadingMark = true;
      })
      .addCase(fetchAutoMark.fulfilled, (state, action) => {
        if (action.payload) {
          state.AutoFilter.listMark = action.payload;
        }
        state.isLoadingMark = false;
      })
      .addCase(fetchAutoModel.pending, state => {
        state.isLoadingModel = true;
      })
      .addCase(fetchAutoModel.fulfilled, (state, action) => {
        if (action.payload) {
          state.AutoFilter.listModel = action.payload;
        }
        state.isLoadingModel = false;
      })
      .addCase(fetchAutoYears.pending, state => {
        state.isLoadingYear = true;
      })
      .addCase(fetchAutoYears.fulfilled, (state, action) => {
        if (action.payload) {
          state.AutoFilter.listYear = action.payload;
        }
        state.isLoadingYear = false;
      })
      .addCase(fetchAutoModification.pending, state => {
        state.isLoadingModification = true;
      })
      .addCase(fetchAutoModification.fulfilled, (state, action) => {
        if (action.payload) {
          state.AutoFilter.listModify = action.payload;
        }
        state.isLoadingModification = false;
      })
      .addCase(fetchDiscsData.pending, state => {
        state.isLoadingDataDiscs = true;
      })
      .addCase(fetchDiscsData.fulfilled, (state, action) => {
        if (action.payload) {
          state.DisksFilter.listRadius = Array.isArray(action.payload.radius) ? action.payload.radius.filter(r => r !== null) : [];
          state.DisksFilter.listPCD = action.payload.pcd;
          state.DisksFilter.listType = action.payload.type.filter(r => r.Id !== null && r.Name !== null);
          state.DisksFilter.listCompany = diskManufacturerSort(action.payload.company);
        }
        state.isLoadingDataDiscs = false;
      })
      .addCase(fetchTyresData.pending, state => {
        state.isLoadingDataTyres = true;
      })
      .addCase(fetchTyresData.fulfilled, (state, action) => {
        if (action.payload) {
          state.TyresFilter.listWidth = action.payload.width;
          state.TyresFilter.listHeight = action.payload.height;
          state.TyresFilter.listRadius = action.payload.radius;
          state.TyresFilter.listCompany = action.payload.company;
        }
        state.isLoadingDataTyres = false;
      })
      .addCase(fetchAkbData.pending, state => {
        state.isLoadingDataAkb = true;
      })
      .addCase(fetchAkbData.fulfilled, (state, action) => {
        if (action.payload) {
          state.AkbFilter.listPower = action.payload.capacity;
          state.AkbFilter.listSize = action.payload.size;
          state.AkbFilter.listPolarity = action.payload.polarity;
          state.AkbFilter.listCompany = action.payload.company;
        }
        state.isLoadingDataAkb = false;
      });
  },
});

export const { setAutoMark, setAutoFilter, setTyresFilter, setDisksFilter, setAkbFilter, setOilsFilter, setTiresParametrsFilter } =
  filterSlice.actions;

export const { actions: filterActions } = filterSlice;

export const mainFilterReducer = filterSlice.reducer;
